<template>
  <v-dialog :value="value" @input="(v) => $emit('input', v)" persistent max-width="900">
    <v-card>
      <v-toolbar depressed flat>
        <span class="headline">Seasonal Pricing</span>
      </v-toolbar>
      <v-card-text>
        <div v-for="(pricePeriod, index) in pricePeriods" :key="index">
          <v-row class="align-center">
            <v-col cols="4" class="d-flex align-center">
              <v-icon small class="mr-2">event</v-icon>
              <month-picker v-model="pricePeriod.mstart" />
              <day-picker :month="pricePeriod.mstart" v-model="pricePeriod.dstart" />
            </v-col>
            <v-col cols="auto">to</v-col>
            <v-col cols="4" class="d-flex align-center">
              <v-icon small class="mr-2">event</v-icon>
              <month-picker v-model="pricePeriod.mend" />
              <day-picker :month="pricePeriod.mend" v-model="pricePeriod.dend" />
            </v-col>
            <v-col cols="3">
              <v-autocomplete
                dense
                outlined
                hide-details
                item-text="label"
                item-value="value"
                :value="pricePeriod.rateType"
                :items="source"
                @input="(v) => updateRateType(index, v)"
              />
            </v-col>
            <v-col cols="auto">
              <v-btn small icon @click="removePricePeriod(index)">
                <v-icon small>clear</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div class="ml-12">
            <v-row v-for="(rate, subIndex) in pricePeriod.rates" :key="subIndex" class="align-center">
              <v-col cols="auto">
                <div>Rate</div>
              </v-col>
              <v-col cols="6" class="d-flex align-center pt-0 pb-0">
                <e-number
                  :env="env"
                  :value="pricePeriod.rates[subIndex]"
                  :type="pricePeriod.rates[subIndex].type"
                  @input="(v) => onPriceChanged(index, subIndex, v)"
                />
                <span class="ml-2">/</span>
                <v-autocomplete
                  class="ml-2"
                  :class="{ 'duplicate-room-type': isDuplicateRoomType(index, subIndex) }"
                  dense
                  outlined
                  :hide-details="isDuplicateRoomType(index, subIndex) ? false : true"
                  item-text="label"
                  item-value="value"
                  :disabled="pricePeriod.rateType != 'PROOM'"
                  :items="seasonPerRoomTypeSource(pricePeriod.rateType, index, subIndex)"
                  :value="pricePeriod.rates[subIndex].type"
                  :error="isDuplicateRoomType(index, subIndex)"
                  :error-messages="isDuplicateRoomType(index, subIndex) ? 'Duplicate room type' : []"
                  @input="(v) => onRoomTypeChanged(index, subIndex, v)"
                />
              </v-col>
              <v-col cols="auto">
                <g-button
                  v-if="subIndex == 0"
                  :disabled="!canGeneratePrices(index)"
                  label="Generate Prices"
                  icon="add"
                  @onclick="generatePrices(index)"
                />
                <v-btn v-else small icon @click="removeRateFromPeriod(index, subIndex)">
                  <v-icon small>clear</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="align-center mb-2">
              <g-button
                v-if="pricePeriods[index].rateType == 'PROOM'"
                label="Add Rate"
                icon="add"
                :disabled="!canAddRateToPeriod(index)"
                @onclick="addRateToPeriod(index)"
              />
            </v-row>
          </div>
        </div>
        <v-row class="align-center mt-2">
          <g-button icon="add" label="Add Period" @onclick="addPricePeriod" />
        </v-row>
      </v-card-text>
      <v-card-actions>
        <div class="flex-grow-1"></div>
        <g-button @onclick="$emit('input', false)" label="Cancel" />
        <g-button type="primary" @onclick="validate" label="Update" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ProductFields, SeasonProductPerRoomTypes, ProductPerRoomTypes } from "@/config/fields/products";
import ENumber from "../../../../components/form/special/ENumber";
import moneyService from "@/services/money";
import utils from "@/modules/products/components/utils";
export default {
  components: {
    ENumber,
  },
  props: {
    value: Boolean,
    priceList: Array,
    env: String,
  },
  data() {
    return {
      pricePeriods: [],
    };
  },
  watch: {
    value: {
      handler(newValue) {
        if (!newValue) return;
        this.pricePeriods = utils.convertToPricePeriods(this.priceList);
      },
    },
  },
  computed: {
    source() {
      var productType = this.get("type");
      var supplierType = this.get("supplier_type");
      return ProductFields.rate_type.getSource(productType, supplierType);
    },
  },
  methods: {
    // Data Manipulation
    addPricePeriod() {
      const productType = this.get("type");
      const supplierType = this.get("supplier_type");

      let defaultRateType;
      if (productType === "ROOM") defaultRateType = "PROOM";
      if (["MEAL", "EXCURSION"].includes(productType)) defaultRateType = "PP";
      if (["RESTAURANT", "EXCURSION"].includes(supplierType)) defaultRateType = "PP";
      if (supplierType === "TRANSPORT") defaultRateType = "PG";

      this.pricePeriods.push({
        rateType: defaultRateType,
        rates: [
          {
            type: productType === "ROOM" ? "DOUBLE_QK" : "DEFAULT",
            value: "0",
            cur: this.env,
          },
        ],
      });
    },

    removePricePeriod(periodIndex) {
      if (this.pricePeriods.length <= 1) return;
      this.pricePeriods = this.pricePeriods.filter((_, i) => i !== periodIndex);
    },

    addRateToPeriod(periodIndex) {
      const rates = this.pricePeriods[periodIndex].rates;
      const nextType = utils.getNextUnusedRoomType(rates.map((r) => r.type));
      if (!nextType) return;
      rates.push({ type: nextType, value: "0", cur: this.env });
    },

    removeRateFromPeriod(periodIndex, rateIndex) {
      const period = this.pricePeriods[periodIndex];
      period.rates = period.rates.filter((_, i) => i !== rateIndex);
    },

    updateRateType(periodIndex, v) {
      const period = this.pricePeriods[periodIndex];
      period.rateType = v;

      const [firstRate] = period.rates;
      firstRate.type = v === "PROOM" ? "DOUBLE_QK" : "DEFAULT";
      period.rates = [firstRate];
    },

    onPriceChanged(periodIndex, rateIndex, v) {
      // ENumber component sets v.type to 'COST' when empty, so we need to clear it.
      if (v.type === "COST") v.type = "";
      this.pricePeriods[periodIndex].rates[rateIndex] = v;
    },

    onRoomTypeChanged(periodIndex, rateIndex, v) {
      const period = this.pricePeriods[periodIndex];
      const usedByOthers = period.rates.map((r, i) => (i === rateIndex ? null : r.type));
      if (v && usedByOthers.includes(v)) {
        this.$root.$error(
          `Room type "${this.roomTypeLabel(v)}" is already used in this period. Each room type can only appear once.`
        );
        return;
      }
      period.rates[rateIndex].type = v;
    },

    isDuplicateRoomType(periodIndex, rateIndex) {
      const period = this.pricePeriods[periodIndex];
      if (!period || period.rateType !== "PROOM") return false;
      const roomType = period.rates[rateIndex] && period.rates[rateIndex].type;
      if (!roomType) return false;
      const dupes = utils.findDuplicateRoomTypes(period.rates.map((r) => r.type));
      return dupes.includes(roomType);
    },

    generatePrices(periodIndex) {
      const rates = this.pricePeriods[periodIndex].rates;
      const doublePrice = rates.find((rate) => rate.type == "DOUBLE_QK");
      const emptyPrice = rates.find((rate) => rate.type == "");

      const basePrice = doublePrice
        ? moneyService.stripFormatting(doublePrice.value)
        : emptyPrice
          ? moneyService.stripFormatting(emptyPrice.value)
          : 0;

      const used = new Set(rates.map((r) => r.type).filter(Boolean));
      ["SINGLE_DK", "DOUBLE_DOUBLE", "TRIPLE", "QUAD"].forEach((type) => {
        if (used.has(type)) return;
        rates.push({
          type,
          value: basePrice + this.getAdditionalPrice(this.env, type),
          cur: this.env,
        });
      });
    },

    getAdditionalPrice(environment, roomType) {
      const priceModifiers = {
        USA: { SINGLE_DK: 0, TRIPLE: 20, DOUBLE_DOUBLE: 20, QUAD: 40 },
        CDA: { SINGLE_DK: 0, TRIPLE: 20, QUAD: 40 },
      };
      return priceModifiers[environment]?.[roomType] || 0;
    },

    // UI Helpers
    canGeneratePrices(periodIndex) {
      return (
        this.pricePeriods[periodIndex].rateType === "PROOM" &&
        this.pricePeriods[periodIndex].rates.length == 1 &&
        ["", "DOUBLE_QK"].includes(this.pricePeriods[periodIndex].rates[0].type)
      );
    },

    canAddRateToPeriod(periodIndex) {
      const rates = this.pricePeriods[periodIndex].rates || [];
      return !!utils.getNextUnusedRoomType(rates.map((r) => r.type));
    },

    roomTypeLabel(value) {
      const match = SeasonProductPerRoomTypes.find((t) => t.value === value);
      return match ? match.label : value;
    },

    // Validation
    validate() {
      if (!this.validateFields()) return;
      if (!this.checkForOverlappingPeriods()) return;
      if (!this.checkForDuplicateRoomTypes()) return;
      this.savePricePeriods();
    },

    checkForDuplicateRoomTypes() {
      for (const period of this.pricePeriods) {
        if (period.rateType !== "PROOM") continue;
        const dupes = utils.findDuplicateRoomTypes(period.rates.map((r) => r.type));
        if (dupes.length) {
          this.$root.$error(
            `Duplicate room type(s) in a season: ${dupes.map((t) => this.roomTypeLabel(t)).join(", ")}. Each room type can only appear once per period.`
          );
          return false;
        }
      }
      return true;
    },

    validateFields() {
      const FIELD_NAMES = {
        mstart: "start month",
        dstart: "start date",
        mend: "end month",
        dend: "end date",
      };

      for (let i = 0; i < this.pricePeriods.length; i++) {
        const pricePeriod = this.pricePeriods[i];

        for (const field in FIELD_NAMES) {
          if (pricePeriod[field] == null) {
            const fieldName = FIELD_NAMES[field];
            this.$root.$error("Missing field detected. Please review your data to ensure all periods are filled.");
            return false;
          }
        }

        for (let j = 0; j < pricePeriod.rates.length; j++) {
          const rate = pricePeriod.rates[j];
          if (!rate.value) {
            this.$root.$error("Missing field detected. Please review your data to ensure all periods are filled.");
            return false;
          }
        }
      }

      return true;
    },

    checkForOverlappingPeriods() {
      const periodsByType = {};

      for (const period of this.pricePeriods) {
        if (!periodsByType[period.rateType]) {
          periodsByType[period.rateType] = [];
        }
        periodsByType[period.rateType].push(period);
      }

      for (const type in periodsByType) {
        const periodsOfType = periodsByType[type];

        for (let i = 0; i < periodsOfType.length; i++) {
          for (let j = 0; j < periodsOfType.length; j++) {
            if (i != j) {
              const p1 = periodsOfType[i];
              const p2 = periodsOfType[j];

              const start_user = parseInt(`${p2.mstart}${p2.dstart < 10 ? "0" + p2.dstart : p2.dstart}`);
              const end_user = parseInt(`${p2.mend}${p2.dend < 10 ? "0" + p2.dend : p2.dend}`);
              const item_start = parseInt(`${p1.mstart}${p1.dstart < 10 ? "0" + p1.dstart : p1.dstart}`);
              const item_end = parseInt(`${p1.mend}${p1.dend < 10 ? "0" + p1.dend : p1.dend}`);

              if (item_start > item_end) {
                if (start_user <= item_end || end_user >= item_start) {
                  this.$root.$error(
                    "Redundant periods detected. Please review your data to ensure that all periods are unique."
                  );
                  return false;
                }
              }
              if (start_user <= item_end && end_user >= item_start) {
                this.$root.$error(
                  "Redundant periods detected. Please review your data to ensure that all periods are unique."
                );
                return false;
              }
            }
          }
        }
      }
      return true;
    },

    // Save & Emit
    savePricePeriods() {
      this.$emit("update", utils.convertToPrices(this.pricePeriods));
      this.$emit("input", false);
    },

    // Utility
    get(val) {
      return this.$store.getters["ProductStore/" + val];
    },

    seasonPerRoomTypeSource(rateType, periodIndex, rateIndex) {
      const base = SeasonProductPerRoomTypes.filter((roomType) =>
        rateType === "PROOM" ? roomType.value !== "DEFAULT" : roomType.value === "DEFAULT"
      );
      if (rateType !== "PROOM" || periodIndex == null || rateIndex == null) return base;

      const period = this.pricePeriods[periodIndex];
      const usedByOthers = (period.rates || []).map((r, i) => (i === rateIndex ? null : r.type));
      const currentValue = period.rates[rateIndex] && period.rates[rateIndex].type;
      return utils.filterAvailableRoomTypeOptions(base, usedByOthers, currentValue);
    },
  },
};
</script>

<style scoped>
.duplicate-room-type >>> .v-input__slot {
  border-color: #d32f2f !important;
}
.duplicate-room-type >>> fieldset {
  border-color: #d32f2f !important;
}
</style>
